import isEmpty from 'lodash/isEmpty';
import { computed, ComputedRef } from 'vue';

import { AccountPermissions } from '@ply-io/ply-front-shared-utilities';

import { account, useIsOveragesEnabled, useAccountAdmins, hasAccountPermission } from '@/store/modules/account/getters';
import { user } from '@/store/modules/auth/getters';
import { setDocsOpen } from '@/store/modules/onboarding/actions';
import { StoreGetter, mapComputedGetter, mapAction, StoreAction } from '@/store/utils/mappers';
import { useBillingActions, useBillingGetters } from '@/store-composables';

const useAndFetchIfNeeded =
	<G>(storeGetter: ComputedRef<G>, fetchActions: (() => Promise<unknown>)[]) =>
	() => {
		if (isEmpty(storeGetter.value)) {
			fetchActions.forEach((fetchAction) => {
				fetchAction();
			});
		}
		return storeGetter;
	};

const useAndFetchOtherIfNeeded =
	<G, T>(storeGetter: ComputedRef<G>, fetchActions: (() => Promise<unknown>)[], valueToReturn: ComputedRef<T>) =>
	() => {
		if (isEmpty(storeGetter.value)) {
			fetchActions.forEach((fetchAction) => {
				fetchAction();
			});
		}
		return valueToReturn;
	};

export const useBilling = () => {
	const {
		getInvoices,
		getProducts,
		getCustomerData,
		getEntitlements,
		setPaymentMethod,
		getPaymentMethod,
		getPaymentIntent,
		getBillingPreview,
		setIsAnnuallyPlan,
		clearPricePreviews,
		deletePaymentMethod,
		upgradeBillingPlans,
		getEntitlementPlans,
		purchaseSingleProduct,
		cancelProductDowngrade,
		getFeatureUsageHistory,
		getSubscriptionHistory,
		setDefaultPaymentMethod,
		getAccountSubscriptions,
		saveBillingEmailsConfig,
		getOrganizationSettings,
		saveOrganizationSettings,
		setOveragesEnabled,
		requestBetaPlansExtension,
		startPromotionalEntitlementsTrial,
		getProductAutomatedSubscriptionChanges,
	} = useBillingActions();
	const {
		usePlans,
		useProduct,
		billingPeriod,
		useEntitlement,
		usePricingPreview,
		useIsAnnuallyPlan,
		useProducts: products,
		usePlanById: planById,
		usePlansByProductType,
		useFeatureUsageHistory,
		useTeamPlans: teamPlans,
		useSubscriptionByProductType,
		useCreditsPlans: creditPlans,
		useEntitlements: entitlements,
		useCustomerData: customerData,
		useCustomerPlans: customerPlans,
		useSubscriptions: subscriptions,
		useHasCapability: hasCapability,
		useFeatureUsageAnalyticsHistory,
		usePaymentMethods: paymentMethods,
		useAutomationPlans: automationPlans,
		useEntitlementPlans: entitlementPlans,
		useCreditsSubscription: creditsSubscription,
		useOrganizationSettings: organizationSettings,
		useEntitlementsLastRefresh: entitlementsLastRefresh,
		useEntitlementsIsRefreshing: entitlementsIsRefreshing,
		useDefaultPaymentMethod: defaultPaymentMethod,
		useDefaultPaymentMethodId: defaultPaymentMethodId,
		useAutomationSubscription: automationSubscription,
		useTeamFeaturesSubscription: teamFeaturesSubscription,
		useCustomerFeatureSubscription: customerFeatureSubscription,
		useUpNextRequiredPlanToUse: upNextRequiredPlanToUse,
		useUpNextRequiredPlanToUseData: upNextRequiredPlanToUseData,
		useProductAutomatedSubscriptionChanges: productAutomatedSubscriptionChanges,
		useOveragesEstimatedCost: getOveragesEstimatedCost,
	} = useBillingGetters();

	const hasPermissionGetter = mapComputedGetter(hasAccountPermission as StoreGetter);
	const canEditBilling = computed(() => hasPermissionGetter.value(AccountPermissions.ChangeBilling));
	const isAnnuallyPlan = computed({
		set: setIsAnnuallyPlan,
		get: () => useIsAnnuallyPlan.value,
	});

	const useProducts = useAndFetchIfNeeded(products, [getProducts]);
	const useEntitlements = useAndFetchIfNeeded(entitlements, [getEntitlements]);
	const useSubscriptions = useAndFetchIfNeeded(subscriptions, [getAccountSubscriptions]);
	const useEntitlementPlans = useAndFetchIfNeeded(entitlementPlans, [getEntitlementPlans]);

	const usePlanById = useAndFetchOtherIfNeeded(usePlans, [getProducts], planById);
	const useHasCapability = useAndFetchOtherIfNeeded(entitlements, [getEntitlements], hasCapability);
	const useEntitlementById = useAndFetchOtherIfNeeded(entitlements, [getEntitlements], useEntitlement);
	const useDefaultPaymentMethod = useAndFetchOtherIfNeeded(paymentMethods, [getPaymentMethod], defaultPaymentMethod);
	const useUpNextRequiredPlanToUse = useAndFetchOtherIfNeeded(entitlementPlans, [getEntitlementPlans], upNextRequiredPlanToUse);
	const useUpNextRequiredPlanToUseData = useAndFetchOtherIfNeeded(entitlementPlans, [getEntitlementPlans], upNextRequiredPlanToUseData);

	return {
		usePlans,
		products,
		teamPlans,
		useProduct,
		creditPlans,
		customerData,
		entitlements,
		billingPeriod,
		customerPlans,
		subscriptions,
		paymentMethods,
		isAnnuallyPlan,
		canEditBilling,
		useEntitlement,
		automationPlans,
		usePricingPreview,
		creditsSubscription,
		entitlementsLastRefresh,
		entitlementsIsRefreshing,
		organizationSettings,
		usePlansByProductType,
		useFeatureUsageHistory,
		defaultPaymentMethodId,
		automationSubscription,
		getOveragesEstimatedCost,
		teamFeaturesSubscription,
		customerFeatureSubscription,
		useSubscriptionByProductType,
		useFeatureUsageAnalyticsHistory,
		productAutomatedSubscriptionChanges,

		user: mapComputedGetter(user as StoreGetter),
		account: mapComputedGetter(account as StoreGetter),
		accountAdmins: mapComputedGetter(useAccountAdmins as StoreGetter),
		isOveragesEnabled: mapComputedGetter(useIsOveragesEnabled as StoreGetter),

		setDocsOpen: mapAction(setDocsOpen as StoreAction),

		useProducts,
		getInvoices,
		getProducts,
		usePlanById,
		getCustomerData,
		getEntitlements,
		useEntitlements,
		useSubscriptions,
		useHasCapability,
		getPaymentIntent,
		setPaymentMethod,
		getPaymentMethod,
		getBillingPreview,
		clearPricePreviews,
		useEntitlementById,
		deletePaymentMethod,
		upgradeBillingPlans,
		useEntitlementPlans,
		purchaseSingleProduct,
		getFeatureUsageHistory,
		getSubscriptionHistory,
		cancelProductDowngrade,
		setDefaultPaymentMethod,
		getAccountSubscriptions,
		saveBillingEmailsConfig,
		getOrganizationSettings,
		useDefaultPaymentMethod,
		saveOrganizationSettings,
		setOveragesEnabled,
		requestBetaPlansExtension,
		useUpNextRequiredPlanToUse,
		useUpNextRequiredPlanToUseData,
		startPromotionalEntitlementsTrial,
		getProductAutomatedSubscriptionChanges,
	};
};
